import { useEffect, useRef, useState } from "react";
import {
  forwardEmail,
  fetchSendGuard,
  ApiError,
  type ReplyResult,
  type SendGuard,
} from "../../api";
import { ContactPicker, type Recipient } from "./ContactPicker";
import { buildOutgoingEmailHtml } from "@edge/shared";

interface Props {
  parent: {
    id: string;
    subject: string;
    body: string;
    fromName: string;
    fromEmail: string;
    receivedAt: string;
  };
  onClose: () => void;
  onSent: (result: ReplyResult) => void;
}

function fmtR(r: Recipient): string {
  return r.name ? `${r.name} <${r.email}>` : r.email;
}

/** Pre-fill the editable forwarded block from the original (the user can trim). */
function buildForwardedPrefill(p: Props["parent"]): string {
  const date = p.receivedAt ? new Date(p.receivedAt).toLocaleString() : "";
  return (
    `From: ${p.fromName}${p.fromEmail ? ` <${p.fromEmail}>` : ""}\n` +
    `Date: ${date}\n` +
    `Subject: ${p.subject}\n\n` +
    (p.body ?? "")
  );
}

/**
 * Forward composer (EM-6 #3). The user picks recipients + an optional comment;
 * the worker's createForward route brings the original body AND its attachments
 * along automatically. Same approve-before-send gate + server send guard as
 * reply/compose — nothing sends until the user confirms.
 */
export function EmailForwardComposer({ parent, onClose, onSent }: Props) {
  const [to, setTo] = useState<Recipient[]>([]);
  const [cc, setCc] = useState<Recipient[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [comment, setComment] = useState("");
  const [subject, setSubject] = useState(
    /^fwd:/i.test(parent.subject) ? parent.subject : `Fwd: ${parent.subject}`,
  );
  const [forwardedBody, setForwardedBody] = useState(() => buildForwardedPrefill(parent));
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guard, setGuard] = useState<SendGuard | null>(null);
  const idemRef = useRef<string>(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : String(Date.now()),
  );

  useEffect(() => {
    fetchSendGuard().then(setGuard).catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, sending]);

  const canSend = to.length > 0 && subject.trim() !== "";

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await forwardEmail(parent.id, {
        to: to.map((r) => ({ email: r.email, name: r.name })),
        cc: cc.map((r) => ({ email: r.email, name: r.name })),
        comment: comment.trim() || undefined,
        subject: subject.trim(),
        forwarded_body: forwardedBody,
        idempotency_key: idemRef.current,
      });
      onSent(result);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "send_failed",
      );
      setSending(false);
    }
  };

  return (
    <div className="write-confirm-backdrop nested-modal" role="dialog" aria-modal="true">
      <div className="write-confirm-card email-reply-card">
        <div className="email-detail-header">
          <div className="write-confirm-title">
            {confirming ? "CONFIRM FORWARD" : "FORWARD"}
          </div>
          <button className="email-detail-close" onClick={onClose} title="Close (Esc)">
            ✕
          </button>
        </div>

        {guard?.active && (
          <div className="email-guard-banner">
            ⚠ SEND GUARD ACTIVE — every send is redirected to{" "}
            <b>{guard.redirect_to}</b>. No email will reach the real recipient.
          </div>
        )}

        {!confirming ? (
          <div className="email-reply-body">
            <ContactPicker label="To" recipients={to} onChange={setTo} />
            {showCc ? (
              <ContactPicker label="Cc" recipients={cc} onChange={setCc} />
            ) : (
              <button className="email-compose-addcc" onClick={() => setShowCc(true)}>
                + Add Cc
              </button>
            )}

            <div className="email-compose-subject-row">
              <span className="contact-picker-label">Subj</span>
              <input
                className="email-compose-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <textarea
              className="email-reply-textarea"
              placeholder="Add a comment (optional)…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
            <div className="email-fwd-body-label">
              Forwarded message (edit / trim anything you don’t want passed along):
            </div>
            <textarea
              className="email-reply-textarea email-fwd-body"
              value={forwardedBody}
              onChange={(e) => setForwardedBody(e.target.value)}
              rows={8}
            />
            <div className="email-reply-sig-note">
              The original’s <b>attachments</b> are forwarded as-is. Your WestEdge
              signature is appended after your comment on send.
            </div>

            {error && <div className="write-confirm-error">⚠ {error}</div>}

            <div className="email-detail-actions">
              <button className="email-detail-close-btn" onClick={onClose}>
                CANCEL
              </button>
              <button
                className="write-confirm-confirm"
                disabled={!canSend}
                onClick={() => setConfirming(true)}
                title={canSend ? "Review before forwarding" : "Add a recipient"}
              >
                REVIEW & FORWARD →
              </button>
            </div>
          </div>
        ) : (
          // ── Approve-before-send gate ──
          <div className="email-reply-body">
            <div className="email-reply-gate-note">
              This is exactly what will be forwarded. Nothing has been sent yet.
            </div>
            <div className="email-reply-meta">
              <div className="email-detail-row">
                <span className="email-detail-label">To</span>
                <span className="email-detail-value">{to.map(fmtR).join(", ")}</span>
              </div>
              {cc.length > 0 && (
                <div className="email-detail-row">
                  <span className="email-detail-label">Cc</span>
                  <span className="email-detail-value">{cc.map(fmtR).join(", ")}</span>
                </div>
              )}
              <div className="email-detail-row">
                <span className="email-detail-label">Subject</span>
                <span className="email-detail-value email-detail-subject">
                  {subject.trim()}
                </span>
              </div>
            </div>

            <div className="email-html-preview">
              <div
                dangerouslySetInnerHTML={{
                  __html: buildOutgoingEmailHtml(comment.trim()),
                }}
              />
              {forwardedBody.trim() && (
                <div className="email-preview-quote">
                  <div className="email-fwd-divider">
                    ---------- Forwarded message ----------
                  </div>
                  <div className="email-fwd-preview-text">{forwardedBody}</div>
                </div>
              )}
            </div>
            <div className="email-reply-sig-note">
              ↳ The original’s attachments are forwarded with this.
            </div>

            {error && <div className="write-confirm-error">⚠ {error}</div>}

            <div className="email-detail-actions">
              <button
                className="email-detail-close-btn"
                onClick={() => setConfirming(false)}
                disabled={sending}
              >
                ← EDIT
              </button>
              <button
                className="write-confirm-confirm"
                onClick={() => void send()}
                disabled={sending}
              >
                {sending ? "FORWARDING…" : "FORWARD NOW"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
